import { useState, useEffect, useRef } from "react";
import footerBg from "@/assets/footer-bg.jpg";

type Party = { id: string; name: string; guests?: string[]; has_responded?: boolean; sketch_data?: string | null };
type Guest = {
  id: string;
  name: string;
  is_primary: boolean;
  is_attending: boolean | null;
  dietary_restrictions: string | null;
  decline_note: string | null;
  accommodation: string | null;
  shuttle_response: string | null;
};

export function RSVP() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Party[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [party, setParty] = useState<Party | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [errors, setErrors] = useState<Record<string, Set<string>>>({});
  const [shaking, setShaking] = useState(false);

  const sketchRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const sketchSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sketchCleared = useRef(false);
  const pendingSketchData = useRef<string | null>(null);
  const partyRef = useRef<Party | null>(null);
  partyRef.current = party;
  const [sketchSaved, setSketchSaved] = useState(false);
  const [savedAttending, setSavedAttending] = useState(false);
  const sketchSavedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function initSketch(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#333";
  }

  function startDraw(e: React.MouseEvent | React.TouchEvent) {
    const canvas = sketchRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    isDrawing.current = true;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const pos = "touches" in e ? e.touches[0] : e;
    ctx.beginPath();
    if (!pos) return;
    ctx.moveTo((pos.clientX - rect.left) * scaleX, (pos.clientY - rect.top) * scaleY);
  }

  function draw(e: React.MouseEvent | React.TouchEvent) {
    if (!isDrawing.current) return;
    const canvas = sketchRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const pos = "touches" in e ? e.touches[0] : e;
    if (!pos) return;
    ctx.lineTo((pos.clientX - rect.left) * scaleX, (pos.clientY - rect.top) * scaleY);
    ctx.stroke();
  }

  function debounceSaveSketch() {
    if (sketchSaveTimer.current) clearTimeout(sketchSaveTimer.current);
    sketchSaveTimer.current = setTimeout(() => saveSketch(), 200);
  }

  function stopDraw() {
    if (isDrawing.current) {
      isDrawing.current = false;
      sketchCleared.current = false;
      debounceSaveSketch();
    }
  }

  function clearSketch() {
    const canvas = sketchRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    sketchCleared.current = true;
  }

  function loadSketchData(dataUrl: string) {
    const canvas = sketchRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = dataUrl;
  }

  function getSketchData(): string | null {
    const canvas = sketchRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const isEmpty = data.data.every((v, i) => i % 4 === 3 ? v === 0 : true);
    if (isEmpty) return null;
    return canvas.toDataURL("image/png");
  }

  async function search() {
    if (query.trim().length < 2) return;
    setSearching(true);
    setHasSearched(true);
    try {
      const res = await fetch(`/api/rsvp/search?q=${encodeURIComponent(query.trim())}`);
      const data = await res.json();
      setResults(data.parties || []);
    } finally {
      setSearching(false);
    }
  }

  async function loadParty(partyId: string) {
    setLoading(true);
    setResults([]);
    setHasSearched(false);
    try {
      const res = await fetch(`/api/rsvp/party/${partyId}`);
      const data = await res.json();
      setParty(data.party);
      pendingSketchData.current = data.party?.sketch_data || null;
      const loadedGuests = (data.guests || []).map((g: Guest) => ({
        ...g,
        is_attending: g.is_attending ?? null,
        dietary_restrictions: g.dietary_restrictions || "",
        decline_note: g.decline_note || "",
        accommodation: g.accommodation || "",
        shuttle_response: g.shuttle_response || "",
      }));
      setGuests(loadedGuests);
      setSavedAttending(loadedGuests.some((g: Guest) => g.is_attending === true));
    } finally {
      setLoading(false);
    }
  }

  async function selectParty(p: Party) {
    loadParty(p.id);
  }

  useEffect(() => {
    fetch("/api/rsvp/saved")
      .then((res) => res.json())
      .then((data) => {
        if (data.partyId) loadParty(data.partyId);
      })
      .catch(() => {});
  }, []);

  function updateGuest(id: string, updates: Partial<Guest>) {
    setGuests((prev) =>
      prev.map((g) => (g.id === id ? { ...g, ...updates } : g))
    );
    if (errors[id]) {
      setErrors((prev) => {
        const next = { ...prev };
        const fields = new Set(next[id]);
        for (const key of Object.keys(updates)) fields.delete(key);
        if (fields.size === 0) delete next[id];
        else next[id] = fields;
        return next;
      });
    }
  }

  async function submit() {
    const attending = guests.filter((g) => g.is_attending === true);
    const newErrors: Record<string, Set<string>> = {};
    for (const g of attending) {
      const fields = new Set<string>();
      if (!g.shuttle_response) fields.add("shuttle_response");
      if (fields.size > 0) newErrors[g.id] = fields;
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const first = attending.find((g) => newErrors[g.id]);
      if (first) {
        showToast(`Please select a shuttle option for ${first.name}.`);
      }
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const res = await fetch("/api/rsvp/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          partyId: party!.id,
          sketchData: getSketchData(),
          guests: guests.map((g) => ({
            id: g.id,
            is_attending: g.is_attending === true,
            dietary_restrictions: g.dietary_restrictions || null,
            decline_note: g.decline_note || null,
            accommodation: g.accommodation || null,
            shuttle_response: g.shuttle_response || null,
          })),
        }),
      });
      const data = await res.json();
      if (data.ok) {
        const anyAttending = guests.some((g) => g.is_attending === true);
        setSavedAttending(anyAttending);
        showToast(anyAttending ? "RSVP submitted! See you there!" : "RSVP submitted! We'll miss you!");
      } else {
        showToast("Something went wrong. Please try again.");
      }
    } catch {
      showToast("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function saveSketch() {
    const p = partyRef.current;
    if (!p) return;
    try {
      const res = await fetch("/api/rsvp/sketch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ partyId: p.id, sketchData: getSketchData() }),
      });
      const data = await res.json();
      if (data.ok) {
        if (sketchSavedTimer.current) clearTimeout(sketchSavedTimer.current);
        setSketchSaved(true);
        sketchSavedTimer.current = setTimeout(() => setSketchSaved(false), 1500);
      }
    } catch {}
  }

  function showToast(msg: string) {
    toastTimers.current.forEach(clearTimeout);
    toastTimers.current = [];
    setToast(msg);
    setToastVisible(true);
    toastTimers.current.push(
      setTimeout(() => {
        setToastVisible(false);
        toastTimers.current.push(
          setTimeout(() => setToast(null), 300)
        );
      }, 5000)
    );
  }

  function reset() {
    setParty(null);
    setGuests([]);
    setQuery("");
    setResults([]);
    setHasSearched(false);
  }

  const hasGuests = guests.length > 0;
  const allAnswered = guests.every((g) => g.is_attending !== null);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <article className="pt-24 md:pt-32 pb-20 mx-auto px-6 max-w-225 flex-1">
        <h1 className="font-display text-4xl text-foreground text-center mb-4">
          Will you be joining us?
        </h1>
        <p className="font-body text-muted-foreground text-center mb-12">
          Please respond by July 15th, 2026.
        </p>

        {/* Search */}
        {!party && (
          <div className="max-w-175 mx-auto space-y-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                search();
              }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name..."
                className="flex-1 rounded-lg border border-input bg-background px-4 py-2 text-foreground placeholder:text-muted-foreground font-body"
              />
              <button
                type="submit"
                disabled={searching || query.trim().length < 2}
                className="rounded-lg bg-primary text-primary-foreground px-5 py-2 font-body hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {searching ? "..." : (
                  <span className="flex items-center gap-4">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search
                  </span>
                )}
              </button>
            </form>

            {results.length > 0 && (
              <div className="space-y-2">
                <p className="font-body text-sm text-muted-foreground">
                  Select your party:
                </p>
                {results.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => selectParty(p)}
                    className="w-full text-left rounded-lg border border-input px-4 py-3 font-body text-foreground hover:bg-muted transition-colors"
                  >
                    {p.name}
                    {p.guests && p.guests.length > 0 && (
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        {p.guests.join(", ")}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {hasSearched && !searching && results.length === 0 && (
              <p className="font-body text-muted-foreground text-center text-sm">
                No parties found. Try a different name.
              </p>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <p className="font-body text-muted-foreground text-center">Loading...</p>
        )}

        {/* Party form */}
        {party && !loading && (
          <div className="space-y-6">
            {party.has_responded && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-center">
                <p className="font-body text-sm text-primary">
                  RSVP submitted!
                </p>
              </div>
            )}
            <div className="text-center space-y-1">
              <h2 className="font-display text-2xl text-foreground">
                {party.name}
              </h2>
              <button
                onClick={reset}
                className="font-body text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors"
              >
                Not your party? Search again
              </button>
            </div>

            <div className="space-y-6">
              {guests.map((g) => (
                <div
                  key={g.id}
                  className="rounded-lg border border-input p-4 space-y-3"
                >
                  <p className="font-body font-bold text-foreground">{g.name}</p>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        updateGuest(g.id, { is_attending: true })
                      }
                      className={`flex-1 rounded-lg border px-4 py-2 font-body text-sm transition-colors ${
                        g.is_attending === true
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-input text-muted-foreground hover:border-foreground hover:text-foreground"
                      }`}
                    >
                      Attending
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        updateGuest(g.id, { is_attending: false })
                      }
                      className={`flex-1 rounded-lg border px-4 py-2 font-body text-sm transition-colors ${
                        g.is_attending === false
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-input text-muted-foreground hover:border-foreground hover:text-foreground"
                      }`}
                    >
                      Can't Make It
                    </button>
                  </div>

                  {g.is_attending === false && (
                    <input
                      type="text"
                      value={g.decline_note || ""}
                      onChange={(e) =>
                        updateGuest(g.id, { decline_note: e.target.value })
                      }
                      placeholder="Send a message (optional)"
                      className="w-full rounded-lg border border-input bg-background px-4 py-2 text-foreground placeholder:text-muted-foreground font-body text-sm"
                    />
                  )}

                  {g.is_attending === true && (
                    <div className="space-y-3">
                      <div>
                        <label className="font-body text-sm text-muted-foreground block mb-1">
                          Where will you be staying? (not sure yet is fine)
                        </label>
                        <input
                          type="text"
                          value={g.accommodation || ""}
                          onChange={(e) =>
                            updateGuest(g.id, {
                              accommodation: e.target.value,
                            })
                          }
                          placeholder="e.g. Portland, Hood River, etc"
                          className="w-full rounded-lg border border-input bg-background px-4 py-2 text-foreground placeholder:text-muted-foreground font-body text-sm"
                        />
                      </div>

                      <div>
                        <label className="font-body text-sm text-muted-foreground block mb-1">
                          Will you be taking the shuttle?
                        </label>
                        <div className="flex gap-2">
                          {["Yes", "No", "Not sure yet"].map((option) => (
                            <button
                              key={option}
                              type="button"
                              onClick={() =>
                                updateGuest(g.id, {
                                  shuttle_response: option,
                                })
                              }
                              className={`flex-1 rounded-lg border px-3 py-2 font-body text-sm transition-colors ${
                                g.shuttle_response === option
                                  ? "border-primary bg-primary/10 text-primary"
                                  : errors[g.id]?.has("shuttle_response")
                                    ? "border-red-500 text-muted-foreground hover:border-foreground hover:text-foreground"
                                    : "border-input text-muted-foreground hover:border-foreground hover:text-foreground"
                              }`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="font-body text-sm text-muted-foreground block mb-1">
                          Any allergies or dietary restrictions? (optional)
                        </label>
                        <input
                          type="text"
                          value={g.dietary_restrictions || ""}
                          onChange={(e) =>
                            updateGuest(g.id, {
                              dietary_restrictions: e.target.value,
                            })
                          }
                          placeholder="Optional"
                          className="w-full rounded-lg border border-input bg-background px-4 py-2 text-foreground placeholder:text-muted-foreground font-body text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={submit}
              disabled={!hasGuests || !allAnswered || submitting}
              className={`w-full rounded-lg bg-primary text-primary-foreground px-5 py-3 font-body hover:opacity-90 transition-opacity disabled:opacity-50 ${shaking ? "shake" : ""}`}
            >
              {submitting ? "Submitting..." : party?.has_responded ? "Update RSVP" : "Submit RSVP"}
            </button>

            {/* Sketch pad */}
            <div className="space-y-2">
              <label className="font-body text-sm text-muted-foreground block text-center">
                Draw us something!
              </label>
              <canvas
                ref={(el) => {
                  (sketchRef as any).current = el;
                  if (el) {
                    initSketch(el);
                    if (pendingSketchData.current) {
                      loadSketchData(pendingSketchData.current);
                      pendingSketchData.current = null;
                    }
                  }
                }}
                width={600}
                height={400}
                className="w-full aspect-3/2 rounded-lg border border-input bg-white touch-none cursor-crosshair"
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={stopDraw}
                onMouseLeave={stopDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={stopDraw}
              />
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={clearSketch}
                  className="font-body text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors"
                >
                  Clear
                </button>
                <span className={`font-body text-xs text-muted-foreground transition-opacity duration-300 ${sketchSaved ? "opacity-100" : "opacity-0"}`}>
                  Autosaved
                </span>
              </div>
            </div>
          </div>
        )}
      </article>

      <img src={footerBg} alt="" className="w-full mt-auto" />

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 bg-foreground text-background px-6 py-3 rounded-lg font-body shadow-lg transition-all duration-300 ${
          toastVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4"
        }`}>
          {toast}
        </div>
      )}
    </div>
  );
}
